import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'stores';

interface OnlineFormState {
  isManaged?: boolean;
  isInvalid: boolean;
  meetingMode: string;
  clientLogged: boolean;
  form: GQL.IContractOnlineForm | null;
}

const initialState: OnlineFormState = {
  isManaged: undefined,
  isInvalid: false,
  meetingMode: '',
  clientLogged: false,
  form: null,
};

export const onlineForm = createSlice({
  name: 'onlineForm',
  initialState,
  reducers: {
    setClientLogged: (state, action: PayloadAction<boolean>) => {
      state.clientLogged = action.payload;
    },
    setInvalidateOnlineForm: (state, action: PayloadAction<boolean>) => {
      state.isInvalid = action.payload;
    },
    setManagedMode: (state, action: PayloadAction<boolean>) => {
      state.isManaged = action.payload === true;
    },
    setMeetingMode: (state, action: PayloadAction<string>) => {
      state.meetingMode = action.payload;
    },
    setOnlineForm: (state, action: PayloadAction<GQL.IContractOnlineForm>) => {
      state.isInvalid = false;
      state.form = action.payload;
    },
    changeFormStep: (state, action: PayloadAction<string>) => {
      if (state.form) {
        state.form.status = action.payload;
      }
    },
  },
});

export const {
  setClientLogged,
  setInvalidateOnlineForm,
  setManagedMode,
  setMeetingMode,
  setOnlineForm,
  changeFormStep,
} = onlineForm.actions;

// selectors
export const selectOnlineFormIsInvalid = (state: RootState): boolean => state.onlineForm.isInvalid;

export const selectOnlineForm = (state: RootState): GQL.IContractOnlineForm | null => state.onlineForm.form;
export const selectOnlineFormData = (state: RootState) => {
  const { contract, data } = state.onlineForm.form || {};
  return {
    agreePovinostPlatby: data?.agreePovinostPlatby || false,
    agreeReklamacniPoriadok: data?.agreeReklamacniPoriadok || false,
    agreeVop: data?.agreeVop || false,
    agreeVzorovyFormularOdstoupeni: data?.agreeVzorovyFormularOdstoupeni || false,
    agreeZacatekPoskytovaniSluzeb: data?.agreeZacatekPoskytovaniSluzeb || false,
    bankAccounts: data?.bankAccounts || false,
    bankAccountsList: data?.bankAccountsList || [],
    blockedBankAccounts: data?.blockedBankAccounts || false,
    chattels: data?.chattels || false,
    chattelsList: data?.chattelsList || [],
    children: data?.children || 0,
    cislo_op: data?.cislo_op || contract?.cislo_op,
    city: data?.city || contract?.mesto,
    client2: data?.client2 || contract?.client2,
    debts: data?.debts || [], // contract?.debts,
    deductionsFromWages: data?.deductionsFromWages || false,
    doublePayments: data?.doublePayments || false,
    installments: data?.installments || false,
    installmentsData: data?.installmentsData || null,
    installmentsPlan: data?.installmentsPlan || null,
    email: data?.email || contract?.email,
    execution: data?.execution || false,
    expensesList: data?.expensesList || [],
    firstName: data?.firstName || contract?.jmeno,
    houseNumber: data?.houseNumber || contract?.cislo_popisne,
    income: data?.income || null,
    incomeValue: data?.incomeValue || null,
    lastName: data?.lastName || contract?.prijmeni,
    living: data?.living || '', // contract?.living,
    mailingAddress: data?.mailingAddress || contract?.koresp_mesto !== '',
    mailingAddressCity: data?.mailingAddressCity || contract?.koresp_mesto,
    mailingAddressHouseNumber: data?.mailingAddressHouseNumber || contract?.koresp_cislo_popisne,
    mailingAddressStreet: data?.mailingAddressStreet || contract?.koresp_ulice,
    mailingAddressZipCode: data?.mailingAddressZipCode || contract?.koresp_psc,
    maritalStatus: data?.maritalStatus || '',
    mobil: data?.mobil || contract?.mobil,
    payment: data?.payment || 0, // contract?.payment,
    paymentDate: data?.paymentDate || '', // contract?.paymentDate,
    paymentMethod: data?.paymentMethod || '', // contract?.paymentMethod,
    productId: data?.productId || 0, // contract?.id_tarif,
    properties: data?.properties || false,
    propertiesList: data?.propertiesList || [],
    rc: data?.rc || contract?.rc,
    secondPayment: data?.secondPayment || 0,
    secondPaymentDate: data?.secondPaymentDate || 0,
    street: data?.street || contract?.ulice,
    zipCode: data?.zipCode || contract?.psc,
  };
};

export const selectOnlineFormContract = (state: RootState) => state.onlineForm.form?.contract;

export const selectConsultantAccess = (state: RootState) => state.onlineForm.isManaged === true;
export const selectClientAccess = (state: RootState) => state.onlineForm.isManaged === false;

export const selectMeetingModeOffline = (state: RootState) => state.onlineForm.meetingMode === 'offline';
export const selectMeetingModeOnline = (state: RootState) => state.onlineForm.meetingMode !== 'offline';

export const selectClientLogged = (state: RootState) => state.onlineForm.clientLogged === true;

export default onlineForm.reducer;
